import { FC } from "react";
import { PropertyGroupSplitBy } from "views/property-groups-configuration/types";
import { propertyGroupTitleMap } from "views/property-groups-configuration/constants";
import { getStateLabel } from "utils/stateCodes";
import { plural } from "pluralize";
import { OptionButton } from "./OptionButton";

interface ExternalOptionsListProps {
  currentStep: PropertyGroupSplitBy;
  inputValue: string;
  isStateMode: boolean;
  stateFilteredOptions: string[];
  placePredictions: any[];
  options: string[];
  selectedOptions: string[];
  disabledOptions: string[];
  optionsWithSegmentMap?: Record<string, string>;
  segmentName?: string;
  isLastStep?: boolean;
  onOptionSelect: (option: string) => void;
  onPredictionSelect: (predictionText: string) => void;
}

export const ExternalOptionsList: FC<ExternalOptionsListProps> = ({
  currentStep,
  inputValue,
  isStateMode,
  stateFilteredOptions,
  placePredictions,
  options,
  selectedOptions,
  disabledOptions,
  optionsWithSegmentMap,
  segmentName,
  isLastStep,
  onOptionSelect,
  onPredictionSelect
}) => {
  if (isStateMode)
    return (
      <div className="mt-1">
        {stateFilteredOptions
          .filter((stateCode) => !options.includes(stateCode))
          .map((stateCode) => {
            const isDisabled =
              disabledOptions.includes(stateCode) ||
              selectedOptions.includes(stateCode);
            return (
              <OptionButton
                key={stateCode}
                option={stateCode}
                displayText={getStateLabel(stateCode)}
                isDisabled={isDisabled}
                onClick={() => !isDisabled && onOptionSelect(stateCode)}
                optionsWithSegmentMap={optionsWithSegmentMap}
                segmentName={segmentName}
                isLastStep={isLastStep}
              />
            );
          })}
        {inputValue && stateFilteredOptions.length === 0 && (
          <div className="py-3 px-4 text-xs text-content-300 text-center">
            No states found matching &quot;{inputValue}
            &quot;
          </div>
        )}
      </div>
    );

  if (inputValue.length > 0 && placePredictions.length > 0)
    return (
      <div className="mt-1">
        {placePredictions
          .filter((prediction) => {
            const suggestionValue = prediction.description.split(",")[0];
            return !options.includes(suggestionValue);
          })
          .reduce(
            (unique, prediction) => {
              const suggestionValue = prediction.description.split(",")[0];
              const isDuplicate = unique.some(
                (item) => item.description.split(",")[0] === suggestionValue
              );
              if (!isDuplicate) unique.push(prediction);
              return unique;
            },
            [] as typeof placePredictions
          )
          .map((prediction) => {
            const suggestionValue = prediction.description.split(",")[0];
            const isDisabled =
              disabledOptions.includes(suggestionValue) ||
              selectedOptions.includes(suggestionValue);

            return (
              <OptionButton
                key={prediction.place_id}
                option={suggestionValue}
                isDisabled={isDisabled}
                onClick={() =>
                  !isDisabled && onPredictionSelect(suggestionValue)
                }
                optionsWithSegmentMap={optionsWithSegmentMap}
                segmentName={segmentName}
                isLastStep={isLastStep}
              />
            );
          })}
      </div>
    );

  return (
    <div className="py-3 px-4 text-xs text-content-300 text-center">
      Type to search for other {plural(propertyGroupTitleMap[currentStep])}
    </div>
  );
};
